import { computed, ref, watch, type Ref } from 'vue'
import type { VirtualMachineSizeOption } from '@/stores/type/calculation.type'

const familyForSize = (option: VirtualMachineSizeOption) => {
  const generation = option.series.match(/^F.*v(\d+)$/)?.[1]
  return generation ? `Fv${generation}` : option.series
}

const familyLabel = (family: string) => family.replace(/^Fv(\d+)$/, 'F-series v$1')
const memoryRatio = (option: VirtualMachineSizeOption) => option.memory_gb / option.cpu_cores

export const useHardwareSelection = (
  vmSizes: Ref<VirtualMachineSizeOption[] | null>,
  region: Ref<string>,
  vmSize: Ref<string>
) => {
  const vmFamily = ref('')
  const vmMemoryRatio = ref<number | null>(null)

  const availableSizes = computed(() =>
    (vmSizes.value ?? []).filter(
      (option) => !option.regions?.length || option.regions.includes(region.value)
    )
  )
  const selectedVmSize = computed(() =>
    availableSizes.value.find((option) => option.size === vmSize.value)
  )
  const vmFamilies = computed(() =>
    [...new Set(availableSizes.value.map(familyForSize))].map((value) => ({
      value,
      title: familyLabel(value)
    }))
  )
  const familySizes = computed(() =>
    availableSizes.value.filter((option) => familyForSize(option) === vmFamily.value)
  )
  const memoryRatios = computed(() =>
    [...new Set(familySizes.value.map(memoryRatio))].sort((left, right) => left - right)
  )
  const hasFixedMemory = computed(() =>
    familySizes.value.length > 0 &&
    familySizes.value.every((option) => option.memory_gb === familySizes.value[0].memory_gb)
  )
  const showMemoryRatio = computed(() => !hasFixedMemory.value && memoryRatios.value.length > 1)
  const matchingSizes = computed(() =>
    familySizes.value.filter(
      (option) => !showMemoryRatio.value || memoryRatio(option) === vmMemoryRatio.value
    )
  )

  const ensureSelection = () => {
    // Preserve an edited profile while its catalogue is still loading.
    if (vmSizes.value === null) return

    if (!vmFamilies.value.some((family) => family.value === vmFamily.value)) {
      const selected = selectedVmSize.value ?? availableSizes.value[0]
      vmFamily.value = selected ? familyForSize(selected) : ''
    }

    if (showMemoryRatio.value) {
      if (vmMemoryRatio.value === null && selectedVmSize.value &&
          familyForSize(selectedVmSize.value) === vmFamily.value) {
        vmMemoryRatio.value = memoryRatio(selectedVmSize.value)
      }
      if (!memoryRatios.value.includes(vmMemoryRatio.value as number)) {
        vmMemoryRatio.value = memoryRatios.value[0] ?? null
      }
    } else {
      vmMemoryRatio.value = null
    }

    if (!matchingSizes.value.some((option) => option.size === vmSize.value)) {
      vmSize.value = matchingSizes.value[0]?.size ?? ''
    }
  }

  const resetSelection = () => {
    vmFamily.value = selectedVmSize.value ? familyForSize(selectedVmSize.value) : ''
    vmMemoryRatio.value = selectedVmSize.value ? memoryRatio(selectedVmSize.value) : null
    ensureSelection()
  }

  watch([vmSizes, region, vmFamily, vmMemoryRatio], ensureSelection, { immediate: true })
  watch(vmSize, () => {
    if (showMemoryRatio.value && selectedVmSize.value &&
        familyForSize(selectedVmSize.value) === vmFamily.value) {
      vmMemoryRatio.value = memoryRatio(selectedVmSize.value)
    }
  })

  return {
    vmFamily,
    vmMemoryRatio,
    vmFamilies,
    memoryRatios,
    showMemoryRatio,
    matchingSizes,
    selectedVmSize,
    resetSelection
  }
}
